import { useState, useCallback, useMemo } from 'react';
import type { Customer, PendingStageChange } from '../types';

export interface UsePipelineModalsReturn {
  // Won modal
  wonModalOpen: boolean;
  setWonModalOpen: (open: boolean) => void;

  // Installation completed modal
  installationModalOpen: boolean;
  setInstallationModalOpen: (open: boolean) => void;

  // Pending stage change (shared by both modals)
  pendingStageChange: PendingStageChange | null;
  setPendingStageChange: (change: PendingStageChange | null) => void;

  // Booking popup
  bookingPopupOpen: boolean;
  setBookingPopupOpen: (open: boolean) => void;
  bookingCustomer: Customer | null;
  setBookingCustomer: (customer: Customer | null) => void;

  // Customer form
  customerFormOpen: boolean;
  setCustomerFormOpen: (open: boolean) => void;

  // Convenience actions
  openBookingPopup: (customer: Customer) => void;
  closeBookingPopup: () => void;
  openWonModal: (change: PendingStageChange) => void;
  openInstallationModal: (change: PendingStageChange) => void;
  closeModals: () => void;
}

/**
 * Hook to manage all pipeline modal states
 * Centralizes modal logic for Won, Installation, Booking, and Customer forms
 */
export function usePipelineModals(): UsePipelineModalsReturn {
  const [wonModalOpen, setWonModalOpen] = useState(false);
  const [installationModalOpen, setInstallationModalOpen] = useState(false);
  const [pendingStageChange, setPendingStageChange] = useState<PendingStageChange | null>(null);
  const [bookingPopupOpen, setBookingPopupOpen] = useState(false);
  const [bookingCustomer, setBookingCustomer] = useState<Customer | null>(null);
  const [customerFormOpen, setCustomerFormOpen] = useState(false);

  const openBookingPopup = useCallback((customer: Customer) => {
    setBookingCustomer(customer);
    setBookingPopupOpen(true);
  }, []);

  const closeBookingPopup = useCallback(() => {
    setBookingPopupOpen(false);
    setBookingCustomer(null);
  }, []);

  const openWonModal = useCallback((change: PendingStageChange) => {
    setPendingStageChange(change);
    setWonModalOpen(true);
  }, []);

  const openInstallationModal = useCallback((change: PendingStageChange) => {
    setPendingStageChange(change);
    setInstallationModalOpen(true);
  }, []);

  const closeModals = useCallback(() => {
    setWonModalOpen(false);
    setInstallationModalOpen(false);
    setPendingStageChange(null);
  }, []);

  return useMemo(
    () => ({
      wonModalOpen,
      setWonModalOpen,
      installationModalOpen,
      setInstallationModalOpen,
      pendingStageChange,
      setPendingStageChange,
      bookingPopupOpen,
      setBookingPopupOpen,
      bookingCustomer,
      setBookingCustomer,
      customerFormOpen,
      setCustomerFormOpen,
      openBookingPopup,
      closeBookingPopup,
      openWonModal,
      openInstallationModal,
      closeModals,
    }),
    [
      wonModalOpen,
      installationModalOpen,
      pendingStageChange,
      bookingPopupOpen,
      bookingCustomer,
      customerFormOpen,
      openBookingPopup,
      closeBookingPopup,
      openWonModal,
      openInstallationModal,
      closeModals,
    ]
  );
}
